import { useState } from 'react';
import { Modal } from '../../../components/ui/Modal/Modal';
import { Button } from '../../../components/ui/Button/Button';
import type { CustomerSummary } from '../../../types/api';
import styles from './CustomerMergeModal.module.css';

export interface CustomerMergeModalProps {
  open: boolean;
  source: CustomerSummary | null;
  target: CustomerSummary | null;
  onConfirm: (sourceUuid: string, targetUuid: string) => void;
  onClose: () => void;
  isLoading?: boolean;
}

type MasterChoice = 'source' | 'target';

export function CustomerMergeModal({
  open,
  source,
  target,
  onConfirm,
  onClose,
  isLoading = false,
}: CustomerMergeModalProps) {
  const [master, setMaster] = useState<MasterChoice>('target');

  if (!source || !target) return null;

  function handleConfirm() {
    if (!source || !target) return;
    // The server always keeps target as master. We swap source/target
    // based on user choice so the surviving record becomes target.
    if (master === 'target') {
      onConfirm(source.uuid, target.uuid);
    } else {
      onConfirm(target.uuid, source.uuid);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Merge Customers"
      size="lg"
      footer={
        <div className={styles.footer}>
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            loading={isLoading}
            disabled={isLoading}
          >
            Merge customers
          </Button>
        </div>
      }
    >
      <div className={styles.body}>
        <p className={styles.warning}>
          Merging combines both records into one. The merged-away record will be
          soft-deleted. All bookings and history are transferred to the surviving
          record. This cannot be undone.
        </p>

        <div className={styles.comparison}>
          <div className={styles.customerCol}>
            <p className={styles.colLabel}>Record A</p>
            <p className={styles.name}>{source.full_name}</p>
            <p className={styles.detail}>{source.customer_number ?? '—'}</p>
            <p className={styles.detail}>{source.phone}</p>
            {source.email ? <p className={styles.detail}>{source.email}</p> : null}
            <p className={styles.detail}>
              {source.address}, {source.postcode}
            </p>
            <p className={styles.detail}>Jobs: {source.total_jobs}</p>
          </div>

          <div className={styles.customerCol}>
            <p className={styles.colLabel}>Record B</p>
            <p className={styles.name}>{target.full_name}</p>
            <p className={styles.detail}>{target.customer_number ?? '—'}</p>
            <p className={styles.detail}>{target.phone}</p>
            {target.email ? <p className={styles.detail}>{target.email}</p> : null}
            <p className={styles.detail}>
              {target.address}, {target.postcode}
            </p>
            <p className={styles.detail}>Jobs: {target.total_jobs}</p>
          </div>
        </div>

        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Which record should survive?</legend>

          <label className={styles.radioRow}>
            <input
              type="radio"
              name="master"
              value="source"
              checked={master === 'source'}
              onChange={() => setMaster('source')}
            />
            <span>Keep {source.full_name} (Record A)</span>
          </label>

          <label className={styles.radioRow}>
            <input
              type="radio"
              name="master"
              value="target"
              checked={master === 'target'}
              onChange={() => setMaster('target')}
            />
            <span>Keep {target.full_name} (Record B)</span>
          </label>
        </fieldset>
      </div>
    </Modal>
  );
}
